import React, { useContext } from 'react';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import Page16Icon from '@atlaskit/icon-object/glyph/page/16';
import type { CreatePayload } from '@atlaskit/link-create';
import { useTenantHasConfluence } from '@atlassian/jira-business-confluence-pages';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { CONTENT_TYPE, type CreateContentModalCoreProps } from '../../../common/types/types';
import { ContentMenuContext } from '../../../controllers/create-content-menu-context';
import { useCreateContentClickBehavior } from '../../../controllers/use-create-content-click-behavior';
import { useEmbeddedPageTracking } from '../../../controllers/use-embedded-page-tracking';
import { convertUrlSpaceIdToSpaceKey, getDraftShareId } from '../../../utils';
import messages from './messages';

export const CreatePageMenuItem = ({
	onCancel,
	onCloseComplete,
	onCreate,
	onFailure,
	onOpenComplete,
	onCreateBehavior,
}: CreateContentModalCoreProps) => {
	const { formatMessage } = useIntl();
	const {
		accessStatus,
		setIsCreateContentModalOpen,
		setCreateContentRuntimeProps,
		setActivePageUrl,
		setIsEpModalOpen,
		embeddedConfluenceSource,
		userHasConfluenceAccess,
	} = useContext(ContentMenuContext);
	const fireAnalyticsEvent = useEmbeddedPageTracking();
	const getCreateContentClickBehavior = useCreateContentClickBehavior();
	const tenantHasConfluence = useTenantHasConfluence();

	const createConfluencePage = () => {
		const openCreateContentModal = () => {
			setIsCreateContentModalOpen(true);
			setCreateContentRuntimeProps({
				isOpen: true,
				contentType: CONTENT_TYPE.PAGE,
				onCancel,
				onCloseComplete,
				onCreate: (payload: CreatePayload): void => {
					onCreate && onCreate(payload);
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					const links = payload.data?.links as Record<string, string>;
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					const spaceKey = payload.data?.spaceKey as string;
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					const spaceId = payload.data?.spaceId as string;

					const linkableUrl = convertUrlSpaceIdToSpaceKey({
						url: payload.url,
						spaceId,
						spaceKey,
					});

					setActivePageUrl(`${linkableUrl}?draftShareId=${getDraftShareId(links?.editui)}`);
					setIsEpModalOpen(true);
				},
				onFailure,
				onOpenComplete,
				onCreateBehavior,
			});
		};

		const ldExperimentEnabled = ff('platform_embed-conf_jira-issue_co-use');
		if (
			ldExperimentEnabled &&
			expValEquals('platform_embed_conf_jira-issue_co-use_exp', 'cohort', 'experiment')
		) {
			// 1) get the spaces
			// 		a) if no space to pick use create content modal
			// 2) create the page with the given space
			// 3) open the EP Modal with the 'publish-modal' allowedFeatures added
		} else {
			openCreateContentModal();
		}
	};

	const onOpen = () => {
		fireAnalyticsEvent({
			action: 'clicked',
			eventName: 'createPageMenuItem',
			embeddedConfluenceSource,
			userHasConfluenceAccess,
			tenantHasConfluence,
			crossJoinStatus: accessStatus,
		});
		return getCreateContentClickBehavior({
			accessStatus,
			createContent: createConfluencePage,
			tenantHasConfluence,
		})();
	};

	return (
		<DropdownItem
			testId="issue-create-confluence-content.ui.create-confluence-content-menu.create-page-menu-item.create-page-menu-item"
			onClick={onOpen}
			elemBefore={<Page16Icon label="" />}
		>
			{formatMessage(messages.createConfluencePageItem)}
		</DropdownItem>
	);
};
